import { useIntl } from "react-intl";
import { DataTableSelectColumn } from "~components/DataTable/components/DataTableSelectColumn/DataTableSelectColumn.tsx";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import type { DataTableRow } from "~components/DataTable/types/dataTableRow.ts";
import { RosterStatuses } from "~enums/rosterStatuses.ts";
import { useSharedRosterCapsColumn } from "~pages/Roster/hooks/useSharedRosterCapsColumn.tsx";
import { useSharedRosterCaptainColumn } from "~pages/Roster/hooks/useSharedRosterCaptainColumn.tsx";
import { useSharedRosterPersonColumns } from "~pages/Roster/hooks/useSharedRosterPersonColumns.tsx";
import { SeasonRosterTableColumns } from "~pages/Roster/SeasonRosterListPage/enums/seasonRosterTableColumns.ts";
import type { SeasonRosterDataTableRow } from "~pages/Roster/SeasonRosterListPage/types/seasonRosterDataTableRow.ts";
import type { UpsertSeasonRosterModel } from "~pages/Roster/SeasonRosterListPage/types/upsertSeasonRosterModel";
import { sortStringValues } from "~utils/utilities.ts";

type Props = {
  massEditMode: boolean;
  isRosterEditable: boolean;
  handleUpdateField: (
    row: SeasonRosterDataTableRow,
    update: Partial<Pick<UpsertSeasonRosterModel, "bib" | "position" | "isCaptain" | "status">>,
  ) => Promise<void>;
  onCaptainToggle: (row: SeasonRosterDataTableRow, isCaptain: boolean) => Promise<void>;
};

type RosterRow = SeasonRosterDataTableRow & DataTableRow;

export const useGetSeasonRosterDataTableColumns = ({
  massEditMode,
  isRosterEditable,
  handleUpdateField,
  onCaptainToggle,
}: Props): Array<DataTableColumn<SeasonRosterDataTableRow>> => {
  const { formatMessage } = useIntl();

  const sharedPersonColumns = useSharedRosterPersonColumns<SeasonRosterDataTableRow>({
    massEditMode,
    handleUpdateField,
  });
  const captainColumn = useSharedRosterCaptainColumn<RosterRow>({
    massEditMode,
    isRosterEditable,
    onCaptainToggle,
  });
  const capsColumn = useSharedRosterCapsColumn<SeasonRosterDataTableRow>();

  const statusOptions = Object.values(RosterStatuses).map((status) => ({ value: status, label: status }));

  const statusColumn: DataTableColumn<SeasonRosterDataTableRow> = {
    key: SeasonRosterTableColumns.Status,
    title: formatMessage({ id: "roster.status" }),
    dataIndex: "status",
    width: 130,
    sorter: (a: SeasonRosterDataTableRow, b: SeasonRosterDataTableRow) => sortStringValues(a.status, b.status),
    render: (value: string | undefined, row: RosterRow) => (
      <DataTableSelectColumn
        value={value}
        isEditMode={massEditMode}
        disabled={row.disabled}
        options={statusOptions}
        onSave={(status) => handleUpdateField(row, { status: status ?? undefined })}
      />
    ),
  };

  return [...sharedPersonColumns, captainColumn, statusColumn, capsColumn];
};
